import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { flushSync } from "react-dom";
import { LegalNotice } from "../auth/LegalNotice";
import { BheliLogo } from "./brand";
import { ChatIcon } from "./icons/Icon";
import { scrollPageTop } from "./scroll";

// Piezas del kit de Bheli (docs/design/BHELI_UI_COMPONENTES.md) para el Platform Admin nuevo. Son las mismas del portal
// nuevo (clases tm-* de styles/kit/team.css, con sus tokens en claro y oscuro), para que una pieza se vea igual en los
// dos productos (V15): encabezado C03, botones C04, campos C05, tarjetas con radio C08, interruptor C09, sección plegable
// C10, toast C13, aviso C14, vacío C18 y barra fija C21.

/** C03: ← a la pantalla de arriba (si la hay), título, subtítulo y acciones a la derecha. */
export function PageHead({
  title,
  sub,
  back,
  tag,
  titleTag,
  children,
}: {
  title: string;
  sub?: ReactNode;
  back?: { href: string; label: string };
  tag?: ReactNode;
  /** Etiqueta a la derecha del título (estado de la empresa, rol de la persona). */
  titleTag?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <header className="tm-head" data-part="encabezado">
      <div className="tm-head-title">
        {back ? (
          <a className="tm-back" href={back.href} aria-label={back.label} title={back.label} data-part="atras">
            <ChatIcon name="arrow-left" />
          </a>
        ) : null}
        <div className="npa-head-text">
          {titleTag ? (
            <div className="npa-head-row">
              <h1 tabIndex={-1}>{title}</h1>
              {titleTag}
            </div>
          ) : (
            <h1 tabIndex={-1}>{title}</h1>
          )}
          {tag || sub ? (
            <p className="tm-head-sub">
              {tag}
              {sub}
            </p>
          ) : null}
        </div>
      </div>
      {children ? <div className="tm-head-actions">{children}</div> : null}
    </header>
  );
}

/** C14: aviso en línea (error con role=alert; los demás, status), con una acción opcional al final del texto. */
export function Notice({
  tone,
  children,
  action,
}: {
  tone: "error" | "warning" | "info" | "done";
  children: ReactNode;
  action?: ReactNode;
}) {
  const kind = tone === "error" ? " error" : tone === "warning" ? " ochre" : "";
  // C14 1.14.8 · un solo dibujo en todo el Admin: ícono de 18 px centrado con la primera línea, texto 13,5 px regular y la
  // acción al borde derecho (npa-notice en admin.css).
  return (
    <div className={`tm-notice npa-notice${kind}`} role={tone === "error" ? "alert" : "status"} data-part="aviso">
      <span className="npa-notice-main">
        <ChatIcon name={tone === "done" ? "check" : tone === "info" ? "info" : "circle-alert"} />
        <span className="npa-notice-text">{children}</span>
      </span>
      {action ? <span className="npa-notice-action">{action}</span> : null}
    </div>
  );
}

/** C18 / C40: estado vacío o de página entera, con su salida. */
export function EmptyState({ title, line, action }: { title: string; line?: string; action?: ReactNode }) {
  return (
    <div className="tm-empty" role="status" data-part="vacio">
      <strong>{title}</strong>
      {line ? <span>{line}</span> : null}
      {action}
    </div>
  );
}

/** C22: mientras llega la primera lectura de una pantalla, una línea discreta (nunca una pantalla en blanco). */
export function Loading({ label }: { label: string }) {
  return (
    <div className="npa-loading" role="status" data-part="cargando">
      <span className="npa-spinner" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

/** Ids que describen un campo: su error y su ayuda, en ese orden. */
export function describedBy(id: string, error?: string | null, help?: ReactNode): string | undefined {
  return (
    [error ? `${id}-error` : null, help && help !== error ? `${id}-help` : null].filter(Boolean).join(" ") || undefined
  );
}

/** C05: etiqueta arriba (con «(opcional)» en gris), el control, el error justo debajo y después la ayuda. */
export function Field({
  id,
  label,
  optional,
  error,
  help,
  field,
  children,
}: {
  id: string;
  label: string;
  optional?: string;
  error?: string | null;
  help?: ReactNode;
  field?: string;
  children: ReactNode;
}) {
  return (
    <div className={`tm-field${error ? " invalid" : ""}`} data-field={field}>
      <label htmlFor={id}>
        {label}
        {optional ? <span className="tm-hint"> {optional}</span> : null}
      </label>
      {children}
      {error ? (
        <span id={`${id}-error`} className="tm-field-error">
          {error}
        </span>
      ) : null}
      {help && help !== error ? (
        <small id={`${id}-help`} className="tm-field-help">
          {help}
        </small>
      ) : null}
    </div>
  );
}

/** Campo de texto C05 completo (etiqueta, campo, error y ayuda). */
export function TextField({
  id,
  field,
  label,
  optional,
  help,
  type = "text",
  value,
  error,
  maxLength,
  autoComplete = "off",
  placeholder,
  onChange,
}: {
  id: string;
  field?: string;
  label: string;
  optional?: string;
  help?: string;
  type?: string;
  value: string;
  error?: string | null;
  maxLength: number;
  autoComplete?: string;
  placeholder?: string;
  onChange: (value: string) => void;
}) {
  return (
    <Field id={id} field={field} label={label} optional={optional} error={error} help={help}>
      <input
        id={id}
        type={type}
        value={value}
        maxLength={maxLength}
        autoComplete={autoComplete}
        placeholder={placeholder}
        spellCheck={false}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={describedBy(id, error, help)}
        onChange={(event) => onChange(event.target.value)}
      />
    </Field>
  );
}

/**
 * Contraseña con el ojo dentro del campo: su nombre dice lo que hará («Mostrar/Ocultar contraseña»). Al enviar el
 * formulario vuelve a ocultarse, para que el gestor de contraseñas reconozca lo que guarda.
 */
export function PasswordInput({
  id,
  name,
  autoComplete,
  inputRef,
  error,
  help,
  showLabel,
  hideLabel,
}: {
  id: string;
  name: string;
  autoComplete: "current-password" | "new-password";
  inputRef: RefObject<HTMLInputElement | null>;
  error?: string | null;
  help?: ReactNode;
  showLabel: string;
  hideLabel: string;
}) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const form = inputRef.current?.form;
    const hide = () => setVisible(false);
    form?.addEventListener("submit", hide);
    return () => form?.removeEventListener("submit", hide);
  }, [inputRef]);
  const label = visible ? hideLabel : showLabel;
  return (
    <span className="tm-secret">
      <input
        id={id}
        name={name}
        type={visible ? "text" : "password"}
        autoComplete={autoComplete}
        autoCapitalize="off"
        spellCheck={false}
        ref={inputRef}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={describedBy(id, error, help)}
      />
      <button
        type="button"
        className={`tm-icon-btn tm-secret-toggle${visible ? " on" : ""}`}
        aria-controls={id}
        aria-label={label}
        aria-pressed={visible}
        title={label}
        onClick={() => setVisible((value) => !value)}
      >
        {/* C54: el ícono dice lo que hará (eye: mostrar; eye-off: ocultar). */}
        <ChatIcon name={visible ? "eye-off" : "eye"} />
      </button>
    </span>
  );
}

/** C09: interruptor con su título y una línea que dice qué pasa en el estado actual. Toda la fila se toca. */
export function Switch({
  title,
  line,
  checked,
  onChange,
}: {
  title: string;
  line?: string | null;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      className="tm-switch"
      aria-checked={checked ? "true" : "false"}
      onClick={() => onChange(!checked)}
      data-part="interruptor"
    >
      <span className="tm-switch-track" aria-hidden="true"></span>
      <span>
        <strong>{title}</strong>
        {line ? <small>{line}</small> : null}
      </span>
    </button>
  );
}

/** C08: tarjetas de opción con radio. Tab entra en la elegida y las flechas mueven y eligen. */
export function OptionCards({
  labelledBy,
  name,
  value,
  options,
  onChange,
}: {
  labelledBy: string;
  name: string;
  value: string;
  options: { value: string; title: string; line: string }[];
  onChange: (value: string) => void;
}) {
  const move = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = ({ ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 } as Record<string, number>)[event.key];
    if (!step) return;
    event.preventDefault();
    const index = options.findIndex((option) => option.value === value);
    const next = options[(index + step + options.length) % options.length]!.value;
    onChange(next);
    requestAnimationFrame(() =>
      document.querySelector<HTMLElement>(`[data-option="${name}-${next}"]`)?.focus({ preventScroll: true }),
    );
  };
  return (
    <div className="tm-options tm-mode-cards" role="radiogroup" aria-labelledby={labelledBy} onKeyDown={move}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className="tm-option"
          role="radio"
          data-option={`${name}-${option.value}`}
          aria-checked={value === option.value ? "true" : "false"}
          tabIndex={value === option.value ? 0 : -1}
          onClick={() => onChange(option.value)}
        >
          <span className="tm-radio" aria-hidden="true" />
          <span className="tm-option-text">
            <strong>{option.title}</strong>
            <small>{option.line}</small>
          </span>
        </button>
      ))}
    </div>
  );
}

/** C10: sección plegable (chevron que rota, título y contenido con sangría). */
export function Collapsible({
  id,
  title,
  open,
  onToggle,
  children,
}: {
  id: string;
  title: string;
  open: boolean;
  onToggle: (open: boolean) => void;
  children: ReactNode;
}) {
  return (
    <section className="npa-collapsible">
      <button
        type="button"
        className="npa-collapsible-head"
        aria-expanded={open ? "true" : "false"}
        aria-controls={id}
        onClick={() => onToggle(!open)}
      >
        <ChatIcon name="chevron-down" />
        <span>{title}</span>
      </button>
      <div className="npa-collapsible-body" id={id} hidden={!open}>
        {children}
      </div>
    </section>
  );
}

/**
 * C21: acciones del formulario; en el celular, barra fija abajo. C27 (E59): mientras está fija, su alto (con el área
 * segura) queda en `--adm-fixed-bar`: el scroll de la página (`main`) termina sobre la barra y lo último queda a 12 px
 * (admin.css), en todas las pantallas con barra fija; nada queda debajo de ella, ni en reposo ni al final.
 */
export function FormActions({ children }: { children: ReactNode }) {
  const bar = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = bar.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const root = document.documentElement;
    const measure = () => {
      if (getComputedStyle(element).position === "fixed")
        root.style.setProperty("--adm-fixed-bar", `${Math.ceil(element.getBoundingClientRect().height)}px`);
      else root.style.removeProperty("--adm-fixed-bar");
    };
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    measure();
    return () => {
      observer.disconnect();
      root.style.removeProperty("--adm-fixed-bar");
    };
  }, []);
  return (
    <div ref={bar} className="tm-form-actions" data-fixed-actions="" data-part="acciones">
      {children}
    </div>
  );
}

// ---- C13 · toast: uno solo, centrado abajo, 2,8 s (con «Deshacer», 6 s: lo que se deshace se aplica al instante, D8) ----
type Undo = { label: string; run: () => void };
type ToastApi = { show: (message: string, undo?: Undo) => void };
const ToastContext = createContext<ToastApi>({ show: () => undefined });

export function ToastScope({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<{ message: string; version: number; undo?: Undo } | null>(null);
  const timer = useRef<number | null>(null);
  const show = useCallback((message: string, undo?: Undo) => {
    if (timer.current) window.clearTimeout(timer.current);
    setToast((current) => ({ message, undo, version: (current?.version ?? 0) + 1 }));
    timer.current = window.setTimeout(() => setToast(null), undo ? 6000 : 2800);
  }, []);
  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );
  const api = useMemo(() => ({ show }), [show]);
  return (
    <ToastContext.Provider value={api}>
      {children}
      {toast ? (
        <div key={toast.version} className="feedback-toast" role="status" aria-live="polite">
          {toast.message}
          {toast.undo ? (
            <button
              type="button"
              className="tm-link npa-toast-undo"
              onClick={() => {
                const run = toast.undo!.run;
                setToast(null);
                run();
              }}
            >
              {toast.undo.label}
            </button>
          ) : null}
        </div>
      ) : null}
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);

// ---- Ingreso y cambio de contraseña (AuthLayout del portal nuevo) ----

/**
 * Marco de las pantallas públicas (ingreso, cambio de contraseña): una tarjeta con la marca del Admin arriba (isotipo de
 * 24 px + «Bheli» + «Admin», D20) y el título. Sin navegación.
 */
export function AuthPage({ title, product, children }: { title: string; product: string; children: ReactNode }) {
  return (
    <main id="main" tabIndex={-1} className="auth-shell npa-auth-shell">
      <div className="tm auth-page npa-auth-card">
        <div className="npa-auth-brand" data-part="marca">
          <BheliLogo variant="isotipo" size={24} theme="auto" />
          <span className="npa-auth-name">Bheli</span>
          <span className="npa-auth-product">{product}</span>
        </div>
        <h1>{title}</h1>
        {children}
      </div>
      <LegalNotice />
    </main>
  );
}

/**
 * Dibuja ya el resultado de un envío y lleva el foco al primer campo con error (C05); sin errores de campo, al título,
 * para que se lea el aviso de arriba.
 */
export function showResult(apply: () => void, hasFieldErrors: boolean) {
  flushSync(apply);
  const field = hasFieldErrors ? document.querySelector<HTMLElement>(".tm [aria-invalid='true']") : null;
  if (field) {
    field.focus();
    return;
  }
  const heading = document.querySelector<HTMLElement>("h1");
  if (heading) {
    if (!heading.hasAttribute("tabindex")) heading.tabIndex = -1;
    heading.focus({ preventScroll: true });
  }
  scrollPageTop();
}
